import React, { useState, useEffect, useCallback } from "react";
import {
    Backdrop, CircularProgress, Box, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, TextField, Typography, Divider, Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import HSTable from "../../components/global/Table";
import Notify from "../../components/global/Notify";
import { getAllWeldHours, updateWeldHours, getWeldHoursSourceFile } from "../../services/tables/WeldHoursTableService";
import { getAllPricesPerHour, updatePricePerHour, translatePricePerHourType } from "../../services/prices/PricePerHourService";

const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("pt-PT");
};

const WeldingPage = () => {
    // ── Preços ────────────────────────────────────────────────────────────────
    const [priceRows, setPriceRows] = useState([]);
    const [priceLoading, setPriceLoading] = useState(false);
    const [editPrice, setEditPrice] = useState(null);
    const [newPrice, setNewPrice] = useState("");
    const [savingPrice, setSavingPrice] = useState(false);

    // ── Horas de Soldadura ────────────────────────────────────────────────────
    const [weldPage, setWeldPage] = useState(0);
    const [weldRowsPerPage, setWeldRowsPerPage] = useState(25);
    const [weldSearch, setWeldSearch] = useState("");
    const [weldRows, setWeldRows] = useState([]);
    const [weldTotal, setWeldTotal] = useState(0);
    const [weldLoading, setWeldLoading] = useState(false);
    const [editWeld, setEditWeld] = useState(null);
    const [weldForm, setWeldForm] = useState({ hoursPerWeld: "", numberOfWorkers: "" });
    const [savingWeld, setSavingWeld] = useState(false);
    const [loadingSource, setLoadingSource] = useState(false);

    const fetchPrices = useCallback(async () => {
        setPriceLoading(true);
        try {
            const res = await getAllPricesPerHour(0, 50, "");
            setPriceRows(res.content);
        } catch (error) {
            if (!error?.silent) Notify.error(error.message);
        } finally {
            setPriceLoading(false);
        }
    }, []);

    const fetchWeldHours = useCallback(async () => {
        setWeldLoading(true);
        try {
            const res = await getAllWeldHours(weldPage, weldRowsPerPage, weldSearch);
            setWeldRows(res.content);
            setWeldTotal(res.totalElements);
        } catch (error) {
            if (!error?.silent) Notify.error(error.message);
        } finally {
            setWeldLoading(false);
        }
    }, [weldPage, weldRowsPerPage, weldSearch]);

    useEffect(() => { fetchPrices(); }, [fetchPrices]);
    useEffect(() => { fetchWeldHours(); }, [fetchWeldHours]);

    // ── Handlers: Preço ───────────────────────────────────────────────────────
    const handleEditPrice = (row) => {
        setEditPrice(row);
        setNewPrice(String(row.pricePerHour));
    };

    const handleSavePrice = async () => {
        const parsed = parseFloat(newPrice);
        if (isNaN(parsed) || parsed <= 0) { Notify.error("Introduza um valor válido."); return; }
        setSavingPrice(true);
        try {
            await updatePricePerHour(editPrice.id, { ...editPrice, pricePerHour: parsed });
            Notify.success("Preço atualizado.");
            setEditPrice(null);
            fetchPrices();
        } catch (error) {
            Notify.error(error.message);
        } finally {
            setSavingPrice(false);
        }
    };

    // ── Handlers: Soldadura ───────────────────────────────────────────────────
    const handleEditWeld = (row) => {
        setEditWeld(row);
        setWeldForm({ hoursPerWeld: String(row.hoursPerWeld), numberOfWorkers: String(row.numberOfWorkers) });
    };

    const handleSaveWeld = async () => {
        const hours = parseFloat(weldForm.hoursPerWeld);
        const workers = parseInt(weldForm.numberOfWorkers, 10);
        if (isNaN(hours) || hours <= 0 || isNaN(workers) || workers <= 0) {
            Notify.error("Introduza valores válidos."); return;
        }
        setSavingWeld(true);
        try {
            await updateWeldHours(editWeld.id, { ...editWeld, hoursPerWeld: hours, numberOfWorkers: workers });
            Notify.success("Entrada atualizada.");
            setEditWeld(null);
            fetchWeldHours();
        } catch (error) {
            Notify.error(error.message);
        } finally {
            setSavingWeld(false);
        }
    };

    const handleViewSource = async () => {
        setLoadingSource(true);
        try {
            const blob = await getWeldHoursSourceFile();
            const url = window.URL.createObjectURL(new Blob([blob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Soldaduras.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            Notify.error(error.message);
        } finally {
            setLoadingSource(false);
        }
    };

    // ── Colunas ───────────────────────────────────────────────────────────────
    const WELD_COLUMNS = [
        { field: "dn", label: "DN", sortable: true, align: "center" },
        { field: "material", label: "Material", sortable: true, align: "center" },
        { field: "hoursPerWeld", label: "H/Soldadura", align: "center", sortable: true, render: (v) => v?.toFixed(3) },
        { field: "numberOfWorkers", label: "Trabalhadores", align: "center" },
        { field: "pricePerHourType", label: "Tipo Preço", align: "center", render: (v) => translatePricePerHourType(v) },
        { field: "updatedAt", label: "Última Atualização", align: "center", render: (v) => formatDate(v) },
        {
            field: "id", label: "Ações", align: "center",
            render: (_, row) => (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditWeld(row); }}>
                    <EditIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    return (
        <>
            {/* ── Preços por Hora ─────────────────────────────────────────── */}
            <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1.5 }}>
                    Preços por Hora
                </Typography>
                <Divider sx={{ mb: 1.5 }} />

                {priceLoading ? (
                    <CircularProgress size={24} sx={{ color: "#04642c" }} />
                ) : (
                    priceRows.map((row) => (
                        <Box key={row.id} sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.75 }}>
                            <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 600 }}>
                                {translatePricePerHourType(row.type)}
                            </Typography>
                            <Typography variant="body2">
                                {row.pricePerHour?.toFixed(2)} €/h
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
                                Atualizado: {formatDate(row.updatedAt)}
                            </Typography>
                            <IconButton size="small" onClick={() => handleEditPrice(row)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))
                )}
            </Paper>

            {/* ── Horas de Soldadura ──────────────────────────────────────── */}
            <Box sx={{ position: "relative", width: "100%", boxShadow: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, pb: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Horas de Soldadura por DN
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={loadingSource ? <CircularProgress size={16} /> : <DescriptionIcon />}
                        onClick={handleViewSource}
                        disabled={loadingSource}
                    >
                        Ver Fonte
                    </Button>
                </Box>
                <HSTable
                    columns={WELD_COLUMNS}
                    data={weldRows}
                    loading={weldLoading}
                    page={weldPage}
                    rowsPerPage={weldRowsPerPage}
                    totalCount={weldTotal}
                    onPageChange={setWeldPage}
                    onRowsPerPageChange={(rpp) => { setWeldRowsPerPage(rpp); setWeldPage(0); }}
                    onSearchChange={(s) => { setWeldSearch(s); setWeldPage(0); }}
                    searchValue={weldSearch}
                    enableSearch
                    rowsPerPageOptions={[10, 25, 50]}
                />
                <Backdrop
                    open={weldLoading}
                    sx={{ position: "absolute", backgroundColor: "rgba(255,255,255,0.8)", color: "#04642c", zIndex: 1 }}
                >
                    <CircularProgress color="inherit" size={40} />
                </Backdrop>
            </Box>

            {/* ── Dialog: editar preço ────────────────────────────────────── */}
            <Dialog open={!!editPrice} onClose={() => setEditPrice(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar Preço/Hora</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Tipo: <strong>{translatePricePerHourType(editPrice?.type)}</strong>
                    </Typography>
                    <TextField
                        label="Preço por Hora (€)"
                        type="number"
                        fullWidth
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        inputProps={{ min: 0, step: 0.5 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditPrice(null)} disabled={savingPrice}>Cancelar</Button>
                    <Button onClick={handleSavePrice} variant="contained" disabled={savingPrice}>
                        {savingPrice ? <CircularProgress size={18} /> : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Dialog: editar soldadura ────────────────────────────────── */}
            <Dialog open={!!editWeld} onClose={() => setEditWeld(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar Entrada — DN {editWeld?.dn}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
                    <TextField
                        label="Horas por Soldadura"
                        type="number"
                        fullWidth
                        value={weldForm.hoursPerWeld}
                        onChange={(e) => setWeldForm((f) => ({ ...f, hoursPerWeld: e.target.value }))}
                        inputProps={{ min: 0, step: 0.001 }}
                        autoFocus
                    />
                    <TextField
                        label="Nº de Trabalhadores"
                        type="number"
                        fullWidth
                        value={weldForm.numberOfWorkers}
                        onChange={(e) => setWeldForm((f) => ({ ...f, numberOfWorkers: e.target.value }))}
                        inputProps={{ min: 1, step: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditWeld(null)} disabled={savingWeld}>Cancelar</Button>
                    <Button onClick={handleSaveWeld} variant="contained" disabled={savingWeld}>
                        {savingWeld ? <CircularProgress size={18} /> : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WeldingPage;